import React from 'react';
import { Card, CardHeader, CardMedia, CardContent, Typography, Box, Stack, Divider } from '@mui/material';
import { LocalActivity, HowToReg } from '@mui/icons-material';
import { IeventUserIsFollowingIsAttending } from '../../redux/activity/types';
import { formatWithOrdinal } from '../../utils/AppUtils';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { CustomChipGroup } from '../Chip/CustomChipGroup';
import CustomButton from '../Button/CustomButton';
import { UserGroup } from '../UserGroup/UserGroup';

interface EventCardProps {
    event: IeventUserIsFollowingIsAttending;
}

const EventSuggestionCard: React.FC<EventCardProps> = ({ event }) => {
    const { eventDetails, userCount, userDetails } = event;
    const { themeStyle } = useThemeContext();

    const maxAvatarDisplay = userDetails && userDetails.length && Math.min(userDetails.length, 4);

    return (
        <Card
            sx={{
                borderRadius: 2,
                backgroundColor: theme[themeStyle].secondaryBg,
                my: 2,
            }}>
            {/* User Info */}
            <CardHeader
                avatar={<ThspianAvatar user={userDetails[0]} />}
                title={
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: theme[themeStyle].tag_white }}>
                        {userDetails[0].firstName}{' '}
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                                color: theme[themeStyle].tag_white,
                            }}>
                            is attending
                        </Typography>
                    </Typography>
                }
                subheader={
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 10, color: theme[themeStyle].tag_white }}>
                        {formatWithOrdinal(eventDetails.event_start_date)}
                    </Typography>
                }
                sx={{ paddingBottom: 0 }}
            />

            {/* Event Image */}
            <Box sx={{ position: 'relative' }}>
                <CardMedia
                    component="img"
                    image={eventDetails.feature_image}
                    alt="Event"
                    sx={{ my: 2, height: '120px', width: '100%' }}
                />

                <Box
                    sx={{
                        // backgroundColor: '#FF6F61',
                        backgroundColor: theme[themeStyle].secondaryBg,
                        boxShadow: 2,
                        borderRadius: 1,
                        fontWeight: 'bold',
                        padding: '4px 8px',
                        fontSize: 12,
                        position: 'absolute',
                        bottom: -10,
                        left: 10,
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 12,
                            color: theme[themeStyle].tag_white,
                        }}>
                        {new Date(eventDetails.event_start_date).getDate()} <br />
                    </Typography>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 12,
                            color: theme[themeStyle].secondary_red,
                        }}>
                        {new Date(eventDetails.event_start_date).toLocaleString('default', { month: 'short' })}
                    </Typography>
                </Box>
            </Box>

            {/* Event Info */}
            <CardContent sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 16,
                                color: theme[themeStyle].tag_white,
                                pb: 1,
                            }}>
                            {eventDetails.name}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                                color: theme[themeStyle].darkGray60,
                                pb: 2,
                            }}>
                            {eventDetails.kind}
                        </Typography>
                        <CustomChipGroup tags={eventDetails.tags!} maxVisible={2} />
                    </Box>
                </Box>

                {/* Attendees */}
                <UserGroup
                    userCount={userCount}
                    users={userDetails}
                    maxAvatarDisplay={maxAvatarDisplay}
                    label="going"
                />
            </CardContent>

            <Divider sx={{ px: 2 }} />
            <Stack
                direction={{
                    xs: 'column',
                    md: 'row',
                }}
                gap={'10px'}
                px={2}
                my={2}>
                <CustomButton
                    label="Register"
                    onClick={() => {}}
                    withIcon
                    customIcon={<HowToReg sx={{ color: theme[themeStyle].zeus_dark, fontSize: 12, mx: 0.5 }} />}
                    sx={{
                        flex: 1,
                    }}
                    textStyle={{
                        fontSize: 12,
                    }}
                />
                <CustomButton
                    label="Get Tickets"
                    onClick={() => {}}
                    withIcon
                    customIcon={
                        <LocalActivity sx={{ color: theme[themeStyle].highlight, mx: 0.5, fontSize: '14px' }} />
                    }
                    buttonProps={{
                        variant: 'text',
                    }}
                    sx={{
                        border: `1px solid ${theme[themeStyle].highlight}`,
                        flex: 1,
                    }}
                    textStyle={{
                        fontSize: 12,
                    }}
                />
            </Stack>
        </Card>
    );
};

export { EventSuggestionCard };
